'use client'

import type { ApolloBrowserXClientValues } from '@/feature/app/services/app.constants'
import { isSSR } from '@/lib/next/services/nextjs.rendering'
import { createReactNativeMaintenanceLink } from '@/lib/react-native/services/react-native.maintenance'
import { from } from '@apollo/client'
import { ApolloClient, InMemoryCache } from '@apollo/client-integration-nextjs'
import type { RuntimeVariables } from '../_helpers/getEnvironmentHeaders'
import { makeBrowserAuthLink } from '../links/makeAuthLink.client'
import { makeErrorLink } from '../links/makeErrorLink'
import { makeBrowserHttpLink } from '../links/makeHttpLink.client'
import { makeApolloLoggerLink } from '../links/makeLogLink.client'
import { makeBrowserSsrMultipartLink } from '../links/makeSsrMultipartLink.client'
import { APOLLO_POSSIBLE_TYPES, APOLLO_TYPE_POLICIES } from '../services/apollo.policies.client'

const needsApolloLogs = process.env.NEXT_PUBLIC_GRAPHQL_LOGS === 'true'

// Temporary workaround, these options are to be used for the queries using apollo client on the client side
// If the client apollo is ran on the server side from a client component, it leads to memory leak
// (The apollo client server side is supposed to be a fresh instance every single time, to be investigated later)
export const APOLLO_CLIENT_SIDE_OPTIONS = {
  skip: isSSR,
  ssr: false,
}

const cache = new InMemoryCache({
  typePolicies: APOLLO_TYPE_POLICIES,
  possibleTypes: APOLLO_POSSIBLE_TYPES,
})

export function makeApolloBrowserClient(
  runtimeVariables: RuntimeVariables,
  xClientValue: ApolloBrowserXClientValues,
) {
  return new ApolloClient({
    cache,
    link: from(
      [
        makeErrorLink(),
        createReactNativeMaintenanceLink(),
        needsApolloLogs && makeApolloLoggerLink(),
        makeBrowserAuthLink(),
        isSSR && makeBrowserSsrMultipartLink(),
        makeBrowserHttpLink(runtimeVariables, xClientValue),
      ].filter((link) => !!link),
    ),
    connectToDevTools: runtimeVariables.apolloClientDevtoolEnabled,
  })
}
